import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * Login composable that provides access to login-related actions and getters
 */
export function useLogin() {
  const store = useStore();

  // Login module getters
  const isValidEmail = computed(() => store.getters['login/GET_IS_VALID_EMAIL']);
  const redirectTo = computed(() => store.getters['login/GET_REDIRECT_PATH']);
  const showPrivacyPolicyModal = computed(() => store.getters['login/GET_PRIVACY_POLICY_MODAL_FLAG']);
  const loggedInUser = computed(() => store.getters['login/GET_LOGGED_IN_USER']);
  const userEmailAddress = computed(() => store.getters['login/GET_USER_EMAIL_ADDRESS']);
  const primaryEmail = computed(() => store.getters['login/GET_USER_PRIMARY_EMAIL']);
  const canBypassReg = computed(() => store.getters['login/CAN_BYPASS_REG']);
  const privacyPolicyErrorMessage = computed(() => store.getters['login/GET_PRIVACY_POLICY_ERROR_MESSAGE']);
  
  // Additional getters for Register and SupplementalRegister components
  const isRegisterPostSuccess = computed(() => store.getters['login/SAVE_NEW_USER']);
  const countries = computed(() => store.getters['login/GET_COUNTRIES']);
  const isMissingInformation = computed(() => store.getters['login/GET_MISSING_INFO_STATUS']);
  const isCompleteProfileSuccess = computed(() => store.getters['login/COMPLETE_PROFILE']);
  const accountTypes = computed(() => store.getters['login/GET_ACCOUNT_TYPES']);
  const companyRoles = computed(() => store.getters['login/GET_COMPANY_ROLES']);
  const companySuggestions = computed(() => store.getters['login/GET_COMPANY_SUGGESTIONS']);
  const isUserPresenter = computed(() => store.getters['login/GET_IS_USER_PRESENTER']);

  // Login actions
  const loginUser = (email) => store.dispatch('login/loginUser', email);
  const onUserRegistrationStatus = (payload) => store.dispatch('login/onUserRegistrationStatus', payload);
  const setPrivacyPolicyModalFlag = (flag) => store.dispatch('login/setPrivacyPolicyModalFlag', flag);
  const updatePrivacyPolicyErrorMessage = (message) => store.dispatch('login/updatePrivacyPolicyErrorMessage', message);
  const checkPrivacyPolicyAcceptance = (payload) => store.dispatch('login/checkPrivacyPolicyAcceptance', payload);
  const checkUserVerifiedHisEmail = () => store.dispatch('login/checkUserVerifiedHisEmail');
  const checkUserEmailVerifiedEmail = () => store.dispatch('login/checkUserEmailVerifiedEmail');
  const getLoggedInUserDetailsWithoutRedirectOnError = () => store.dispatch('login/getLoggedInUserDetailsWithoutRedirectOnError');
  const savePrivacyPolicyConsent = (data) => store.dispatch('login/savePrivacyPolicyConsent', data);
  
  // Additional actions for Register and SupplementalRegister components
  const saveNewUser = (payload) => store.dispatch('login/saveNewUser', payload);
  const setEmailAddress = (email) => store.dispatch('login/setEmailAddress', email);
  const fetchCountries = () => store.dispatch('login/fetchCountries');
  const fetchMissingInformationStatus = (email) => store.dispatch('login/fetchMissingInformationStatus', email);
  const completeUserProfile = (payload) => store.dispatch('login/completeUserProfile', payload);
  const getRedirectAfterProfileCompletion = () => store.dispatch('login/getRedirectAfterProfileCompletion');
  const fetchAccountTypes = () => store.dispatch('login/fetchAccountTypes');
  const fetchCompanyRoles = (payload) => store.dispatch('login/fetchCompanyRoles', payload);
  const fetchCompanySuggestions = (email) => store.dispatch('login/fetchCompanySuggestions', email);
  const fetchPresenterRegBypass = (payload) => store.dispatch('login/fetchPresenterRegBypass', payload);

  return {
    // Getters
    isValidEmail,
    redirectTo,
    showPrivacyPolicyModal,
    loggedInUser,
    userEmailAddress,
    primaryEmail,
    canBypassReg,
    privacyPolicyErrorMessage,
    isRegisterPostSuccess,
    countries,
    isMissingInformation,
    isCompleteProfileSuccess,
    accountTypes,
    companyRoles,
    companySuggestions,
    isUserPresenter,
    
    // Actions
    loginUser,
    onUserRegistrationStatus,
    setPrivacyPolicyModalFlag,
    updatePrivacyPolicyErrorMessage,
    checkPrivacyPolicyAcceptance,
    checkUserVerifiedHisEmail,
    checkUserEmailVerifiedEmail,
    getLoggedInUserDetailsWithoutRedirectOnError,
    savePrivacyPolicyConsent,
    saveNewUser,
    setEmailAddress,
    fetchCountries,
    fetchMissingInformationStatus,
    completeUserProfile,
    getRedirectAfterProfileCompletion,
    fetchAccountTypes,
    fetchCompanyRoles,
    fetchCompanySuggestions,
    fetchPresenterRegBypass,
  };
}
